import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../services/api';
import logoFull from '../assets/logo.png';
import logoIcon from '../assets/logo-icon.svg';

// ============ TYPES ============
interface MobileUser {
  id: string; nom: string; prenom: string; email: string; telephone: string;
  role: string; partenaire_id: string; partenaire_nom: string;
}
interface Abonne {
  id: string; reference: string; prenomnom: string; prenom: string; nom: string;
  telephone: string; compteur: string; lastindex: number; datelastindex: string;
  resteapayer: number; secteur_id: string; localite_id: string;
  categorieabonne_id: string; categorie: string; secteur: string; localite: string;
}
interface Secteur { id: string; libelle: string; forage: string; }
interface Localite { id: string; libelle: string; }
interface PendingReleve {
  abonne_id: string; mois: number; annee: number; newindex: number;
  datenewindex: string; abonne_nom: string; oldindex: number; timestamp: number; sent?: boolean;
}
interface MediaAttachment { type: 'image' | 'video' | 'audio'; dataUrl: string; name: string; size: number; }
interface PendingSignal { type: string; comment: string; location: string; timestamp: number; media?: MediaAttachment[]; }
interface PendingPaiement {
  id: string; factureref: string; montant: number; mois: number; annee: number;
  periode: string; abonne: string; datepaiement: string; moyenpaiement: string;
  timestamp: number; sent?: boolean;
}

// ============ STORAGE ============
const SK = {
  TOKEN: 'forgest_mobile_token', USER: 'forgest_mobile_user',
  ABONNES: 'forgest_mobile_abonnes', SECTEURS: 'forgest_mobile_secteurs',
  LOCALITES: 'forgest_mobile_localites', PARTENAIRE: 'forgest_mobile_partenaire',
  SYNC_DATE: 'forgest_mobile_sync_date', PENDING_RELEVES: 'forgest_mobile_pending_releves',
  PENDING_SIGNALS: 'forgest_mobile_pending_signals', PENDING_PAIEMENTS: 'forgest_mobile_pending_paiements',
  RELEVES_STATUS: 'forgest_mobile_releves_status', REMEMBER: 'forgest_mobile_remember',
};
function store(key: string, data: any) { localStorage.setItem(key, JSON.stringify(data)); }
function load<T>(key: string, fb: T): T { try { const d = localStorage.getItem(key); return d ? JSON.parse(d) : fb; } catch { return fb; } }

// ============ BILLING MONTH ============
const MOIS = ['', 'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
function getBilling(): { mois: number; annee: number; label: string } {
  const n = new Date(); let m: number, a: number;
  if (n.getDate() >= 20) { m = n.getMonth() + 1; a = n.getFullYear(); }
  else { m = n.getMonth(); a = n.getFullYear(); if (m === 0) { m = 12; a--; } }
  return { mois: m, annee: a, label: `${MOIS[m]} ${a}` };
}
function parseFrMonth(s: string): { mois: number; annee: number } | null {
  if (!s) return null;
  const mm: Record<string, number> = { janvier: 1, fevrier: 2, 'f\u00e9vrier': 2, mars: 3, avril: 4, mai: 5, juin: 6, juillet: 7, aout: 8, 'ao\u00fbt': 8, septembre: 9, octobre: 10, novembre: 11, decembre: 12, 'd\u00e9cembre': 12 };
  const p = s.trim().toLowerCase().split(/\s+/);
  if (p.length >= 2) { const m = mm[p[0]], y = parseInt(p[1]); if (m && y) return { mois: m, annee: y }; }
  return null;
}

// ============ DESIGN SYSTEM ============
const C = {
  primary: '#2196F3', primaryDark: '#1976D2', primaryLight: '#42A5F5',
  accent: '#E8943A', accentLight: '#F5C78A', teal: '#42A5F5', tealLight: '#64B5F6',
  orange: '#E8943A', blue: '#2196F3',
  text: '#1E293B', textSec: '#475569', textMuted: '#9CA3AF',
  bg: '#FFFFFF', surface: '#F9FAFB', border: '#E5E7EB',
  white: '#FFFFFF', success: '#059669', error: '#DC2626', warning: '#D97706',
  rose: '#E11D48',
  gradGreen: 'linear-gradient(135deg, #1976D2, #42A5F5)',
  gradTeal: 'linear-gradient(135deg, #E8943A, #F5A623)',
  gradOrange: 'linear-gradient(135deg, #E8943A, #F59E0B)',
  gradRose: 'linear-gradient(135deg, #E11D48, #BE123C)',
  gradSuccess: 'linear-gradient(135deg, #059669, #047857)',
};
const card: React.CSSProperties = { background: C.white, border: `1px solid ${C.border}`, borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };

// ============ CSS KEYFRAMES ============
const STYLES_ID = 'forgest-mobile-styles';
function injectStyles() {
  if (document.getElementById(STYLES_ID)) return;
  const s = document.createElement('style'); s.id = STYLES_ID;
  s.textContent = `
    @keyframes fg-scale { 0% { transform: scale(0.9); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
    @keyframes fg-slide-up { 0% { transform: translateY(20px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
    @keyframes fg-check { 0% { transform: scale(0) rotate(-45deg); } 50% { transform: scale(1.2) rotate(0); } 100% { transform: scale(1) rotate(0); } }
    @keyframes fg-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
    @keyframes fg-spin { 0% { transform: rotate(0); } 100% { transform: rotate(360deg); } }
    .fg-card:active { transform: scale(0.97); }
    .fg-btn:active { transform: scale(0.95); }
    .fg-input:focus { border-color: ${C.primary} !important; box-shadow: 0 0 0 3px rgba(33,150,243,0.1) !important; }
    * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
  `;
  document.head.appendChild(s);
}

// ============ SHARED UI (outside render to avoid re-creation) ============
const FgInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input {...props} className="fg-input" style={{ width: '100%', padding: '13px 16px', background: C.white, border: `1px solid ${C.border}`,
    borderRadius: 12, fontSize: 14, color: C.text, outline: 'none', boxSizing: 'border-box', transition: 'all 0.2s', ...props.style }} />
);
const FgBtn: React.FC<{ label: string; bg?: string; onClick?: () => void; disabled?: boolean; full?: boolean; icon?: string; color?: string }> = ({ label, bg, onClick, disabled, full, icon, color }) => (
  <button onClick={onClick} disabled={disabled} className="fg-btn" style={{
    width: full ? '100%' : undefined, padding: '14px 28px', background: disabled ? C.surface : (bg || C.gradGreen),
    color: disabled ? C.textMuted : (color || C.white), border: 'none', borderRadius: 14, fontSize: 15, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
    transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, letterSpacing: 0.3 }}>
    {icon && <span>{icon}</span>}{label}
  </button>
);
const FgHeader: React.FC<{ title: string; onBack?: () => void; green?: boolean }> = ({ title, onBack, green }) => (
  <div style={{ background: green ? C.primaryDark : C.white, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14,
    position: 'sticky', top: 0, zIndex: 100, borderBottom: green ? 'none' : `1px solid ${C.border}` }}>
    {onBack && <button onClick={onBack} className="fg-btn" style={{ background: green ? 'rgba(255,255,255,0.15)' : C.surface, border: 'none',
      color: green ? C.white : C.text, width: 36, height: 36, borderRadius: 10, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{'\u2190'}</button>}
    <span style={{ fontSize: 16, fontWeight: 700, color: green ? C.white : C.text, flex: 1 }}>{title}</span>
  </div>
);
const FgBadge: React.FC<{ n: number; style?: React.CSSProperties }> = ({ n, style: s }) => n > 0 ? (
  <span style={{ position: 'absolute', top: -4, right: -4, background: C.error, color: C.white, borderRadius: 10, minWidth: 18, height: 18, fontSize: 10, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', ...s }}>{n}</span>
) : null;
const PinInput: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 12, position: 'relative' }} onClick={() => inputRef.current?.focus()}>
      {[0, 1, 2, 3].map(i => (
        <div key={i} style={{ width: 52, height: 58, borderRadius: 14, border: `2px solid ${value.length === i ? C.primary : value.length > i ? C.primaryLight : C.border}`,
          background: value.length > i ? `${C.primary}08` : C.white, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 24, fontWeight: 800, color: C.text, transition: 'all 0.2s',
          boxShadow: value.length === i ? `0 0 0 3px ${C.primary}20` : 'none' }}>
          {value[i] ? '\u2022' : ''}
        </div>
      ))}
      <input ref={inputRef} type="tel" inputMode="numeric" pattern="[0-9]*" maxLength={4} value={value}
        onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 4); onChange(v); }}
        style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', top: 0, left: 0, fontSize: 16 }}
        autoComplete="off" />
    </div>
  );
};

// ============ STABLE UI COMPONENTS (outside render to prevent re-mount) ============
const FgToast: React.FC<{ msg: { text: string; type: string }; showNav: boolean }> = ({ msg, showNav }) => msg.text ? (
  <div style={{ position: 'fixed', bottom: showNav ? 90 : 30, left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
    padding: '12px 28px', borderRadius: 14, fontSize: 13, fontWeight: 600, boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
    background: msg.type === 'error' ? C.gradRose : msg.type === 'success' ? C.gradSuccess : C.gradGreen, color: C.white,
    animation: 'fg-slide-up 0.3s ease' }}>{msg.text}</div>
) : null;

const FgLoader: React.FC<{ loading: boolean }> = ({ loading }) => loading ? (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
    <div style={{ ...card, padding: '36px 48px', textAlign: 'center', animation: 'fg-scale 0.3s ease', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
      <div style={{ width: 40, height: 40, border: `3px solid ${C.surface}`, borderTopColor: C.primary, borderRadius: '50%', margin: '0 auto 14px',
        animation: 'fg-spin 0.8s linear infinite' }} />
      <p style={{ color: C.textSec, fontSize: 13, margin: 0 }}>Chargement...</p>
    </div>
  </div>
) : null;

const FgOffline: React.FC<{ isOnline: boolean }> = ({ isOnline }) => !isOnline ? (
  <div style={{ background: '#FEF3C7', padding: '8px 16px', fontSize: 11, color: '#92400E', textAlign: 'center', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#F59E0B', display: 'inline-block' }} /> Mode hors-ligne
  </div>
) : null;

const FgBottomNav: React.FC<{ show: boolean; activeTab: string; onScan: () => void; onHome: () => void; onCollecte: () => void; onPaiements: () => void; onProfil: () => void }> =
  ({ show, activeTab, onScan, onHome, onCollecte, onPaiements, onProfil }) => !show ? null : (
  <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 200 }}>
    <svg viewBox="0 0 400 76" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, width: '100%', height: 76, filter: 'drop-shadow(0 -3px 10px rgba(0,0,0,0.08))' }} preserveAspectRatio="none">
      <path d="M16,8 L155,8 C162,8 166,10 170,18 Q180,42 200,42 Q220,42 230,18 C234,10 238,8 245,8 L384,8 Q400,8 400,24 L400,76 L0,76 L0,24 Q0,8 16,8 Z" fill="white"/>
    </svg>
    <button onClick={onScan} className="fg-btn"
      style={{ position: 'absolute', left: '50%', top: -24, transform: 'translateX(-50%)', width: 62, height: 62, borderRadius: '50%',
        background: C.primary, border: `5px solid ${C.white}`, color: C.white, fontSize: 22, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(33,150,243,0.4)', padding: 0, zIndex: 210 }}>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M4 7V4h3M20 7V4h-3M4 17v3h3M20 17v3h-3" /><rect x="7" y="7" width="10" height="10" rx="1" /></svg>
    </button>
    <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', padding: '0 4px max(env(safe-area-inset-bottom, 8px), 8px)', height: 64, zIndex: 205 }}>
      {[
        { id: 'home', label: 'Accueil', action: onHome,
          icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
        { id: 'collecte', label: 'Releve', action: onCollecte,
          icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> },
        { id: '_spacer', label: '', action: () => {}, icon: null },
        { id: 'paiements', label: 'Paiements', action: onPaiements,
          icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg> },
        { id: 'profil', label: 'Profil', action: onProfil,
          icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
      ].map(tab => tab.id === '_spacer' ? (
        <div key="_spacer" style={{ width: 62 }} />
      ) : (
        <button key={tab.id} onClick={tab.action} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 3, padding: '4px 14px', color: activeTab === tab.id ? C.primary : '#64748B', transition: 'color 0.2s' }}>
          <span style={{ color: activeTab === tab.id ? C.primary : '#64748B' }}>{tab.icon}</span>
          <span style={{ fontSize: 12, fontWeight: activeTab === tab.id ? 700 : 600, letterSpacing: 0.2 }}>{tab.label}</span>
          {activeTab === tab.id && <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.primary, marginTop: -1 }} />}
        </button>
      ))}
    </div>
  </div>
);

const FgPageWrap: React.FC<{ children: React.ReactNode; showNav: boolean; activeTab: string; msg: { text: string; type: string }; loading: boolean; onScan: () => void; onHome: () => void; onCollecte: () => void; onPaiements: () => void; onProfil: () => void }> =
  ({ children, showNav, activeTab, msg, loading, onScan, onHome, onCollecte, onPaiements, onProfil }) => (
  <div style={{ minHeight: '100vh', background: C.bg, paddingBottom: showNav ? 82 : 0 }}>
    {children}
    <FgBottomNav show={showNav} activeTab={activeTab} onScan={onScan} onHome={onHome} onCollecte={onCollecte} onPaiements={onPaiements} onProfil={onProfil} />
    <FgToast msg={msg} showNav={showNav} />
    <FgLoader loading={loading} />
  </div>
);

// ============ MAIN COMPONENT ============
const Mobile: React.FC = () => {
  type Screen = 'login' | 'home' | 'gestionnaire' | 'localites' | 'secteurs' | 'abonnes'
    | 'releve' | 'releves' | 'releve-detail' | 'signalisations'
    | 'paiements' | 'encaisser' | 'upload' | 'upload-ok';

  const [screen, setScreen] = useState<Screen>('login');
  const [user, setUser] = useState<MobileUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [abonnes, setAbonnes] = useState<Abonne[]>([]);
  const [secteurs, setSecteurs] = useState<Secteur[]>([]);
  const [localites, setLocalites] = useState<Localite[]>([]);
  const [partenaire, setPartenaire] = useState<any>(null);
  const [syncDate, setSyncDate] = useState('');
  const [pendingReleves, setPendingReleves] = useState<PendingReleve[]>([]);
  const [pendingSignals, setPendingSignals] = useState<PendingSignal[]>([]);
  const [pendingPaiements, setPendingPaiements] = useState<PendingPaiement[]>([]);
  const [relevesStatus, setRelevesStatus] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [selectedSecteur, setSelectedSecteur] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAbonne, setSelectedAbonne] = useState<Abonne | null>(null);
  const [selectedReleve, setSelectedReleve] = useState<PendingReleve | null>(null);
  const [newIndex, setNewIndex] = useState('');
  const [signalType, setSignalType] = useState('incident');
  const [signalComment, setSignalComment] = useState('');
  const [signalMedia, setSignalMedia] = useState<MediaAttachment[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const [loginPhone, setLoginPhone] = useState('');
  const [loginPwd, setLoginPwd] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPwdChange, setShowPwdChange] = useState(false);
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [uploadResult, setUploadResult] = useState({ releves: 0, signals: 0, paiements: 0 });
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState<any>(null);
  const [payMethod, setPayMethod] = useState('espece');
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scanDivId = useRef('qr-' + Date.now());
  const [pFilterMois, setPFilterMois] = useState(new Date().getMonth() + 1);
  const [pFilterAnnee, setPFilterAnnee] = useState(new Date().getFullYear());
  const [pSearch, setPSearch] = useState('');

  useEffect(() => { injectStyles(); }, []);
  useEffect(() => {
    const on = () => setIsOnline(true), off = () => setIsOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  useEffect(() => {
    const t = load<string | null>(SK.TOKEN, null), u = load<MobileUser | null>(SK.USER, null);
    if (load<boolean>(SK.REMEMBER, false)) setRemember(true);
    if (t && u) {
      setToken(t); setUser(u); setScreen('home');
      setAbonnes(load(SK.ABONNES, [])); setSecteurs(load(SK.SECTEURS, []));
      setLocalites(load(SK.LOCALITES, [])); setPartenaire(load(SK.PARTENAIRE, null));
      setSyncDate(load(SK.SYNC_DATE, '')); setPendingReleves(load(SK.PENDING_RELEVES, []));
      setPendingSignals(load(SK.PENDING_SIGNALS, [])); setPendingPaiements(load(SK.PENDING_PAIEMENTS, []));
      setRelevesStatus(load(SK.RELEVES_STATUS, {}));
    }
  }, []);

  const showMsg = (text: string, type = 'info') => { setMsg({ text, type }); setTimeout(() => setMsg({ text: '', type: '' }), 3500); };
  const go = (s: Screen) => setScreen(s);

  // ============ AUTH ============
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOnline) { showMsg('Connexion internet requise', 'error'); return; }
    setLoading(true);
    try {
      const res = await api.post('/mobile/login', { login: loginPhone, password: loginPwd });
      setToken(res.data.token); setUser(res.data.user);
      store(SK.TOKEN, res.data.token); store(SK.USER, res.data.user); store(SK.REMEMBER, remember);
      setScreen('home'); showMsg('Bienvenue !', 'success');
    } catch (err: any) { showMsg(err.response?.data?.error || 'Erreur de connexion', 'error'); }
    setLoading(false);
  };
  const handleLogout = () => {
    Object.values(SK).forEach(k => localStorage.removeItem(k));
    setToken(null); setUser(null); setAbonnes([]); setSecteurs([]);
    setPendingReleves([]); setPendingSignals([]); setPendingPaiements([]);
    setScreen('login');
  };

  // ============ SYNC ============
  const handleSync = async () => {
    if (!isOnline) { showMsg('Pas de connexion', 'error'); return; }
    setLoading(true);
    try {
      const h = { Authorization: `Bearer ${token}` };
      const res = await api.get('/mobile/sync-data', { headers: h });
      const d = res.data;
      setAbonnes(d.abonnes); store(SK.ABONNES, d.abonnes);
      setSecteurs(d.secteurs); store(SK.SECTEURS, d.secteurs);
      setLocalites(d.localites); store(SK.LOCALITES, d.localites);
      setPartenaire(d.partenaire); store(SK.PARTENAIRE, d.partenaire);
      const now = new Date().toISOString(); setSyncDate(now); store(SK.SYNC_DATE, now);
      const b = getBilling();
      const sr = await api.get('/mobile/releves-status', { params: { mois: b.mois, annee: b.annee }, headers: h });
      setRelevesStatus(sr.data); store(SK.RELEVES_STATUS, sr.data);
      showMsg(`${d.abonnes.length} abonnes telecharges`, 'success');
    } catch (err: any) { showMsg(err.response?.data?.error || 'Erreur synchro', 'error'); }
    setLoading(false);
  };

  // ============ UPLOAD ============
  const handleUpload = async () => {
    if (!isOnline) { showMsg('Pas de connexion', 'error'); return; }
    const uR = pendingReleves.filter(r => !r.sent), uP = pendingPaiements.filter(p => !p.sent);
    if (!uR.length && !pendingSignals.length && !uP.length) { showMsg('Rien a envoyer', 'info'); return; }
    setLoading(true);
    try {
      const h = { Authorization: `Bearer ${token}` }; let rC = 0, sC = 0, pC = 0;
      if (uR.length) { const r = await api.post('/mobile/upload-releves', { releves: uR }, { headers: h }); rC = r.data.inserted;
        const up = pendingReleves.map(r2 => uR.some(u => u.timestamp === r2.timestamp) ? { ...r2, sent: true } : r2); setPendingReleves(up); store(SK.PENDING_RELEVES, up); }
      if (uP.length) { const r = await api.post('/mobile/upload-paiements', { paiements: uP }, { headers: h }); pC = r.data.inserted;
        const up = pendingPaiements.map(p => uP.some(u => u.timestamp === p.timestamp) ? { ...p, sent: true } : p); setPendingPaiements(up); store(SK.PENDING_PAIEMENTS, up); }
      if (pendingSignals.length) { const r = await api.post('/mobile/upload-signalisations', { signalisations: pendingSignals }, { headers: h }); sC = r.data.inserted;
        setPendingSignals([]); store(SK.PENDING_SIGNALS, []); }
      setUploadResult({ releves: rC, signals: sC, paiements: pC }); setScreen('upload-ok');
    } catch (err: any) { showMsg(err.response?.data?.error || "Erreur d'envoi", 'error'); }
    setLoading(false);
  };

  const handleChangePwd = async () => {
    if (!isOnline) { showMsg('Connexion requise', 'error'); return; }
    if (newPwd !== confirmPwd) { showMsg('Les codes PIN ne correspondent pas', 'error'); return; }
    if (!/^\d{4}$/.test(newPwd)) { showMsg('Le code PIN doit faire 4 chiffres', 'error'); return; }
    setLoading(true);
    try { await api.post('/mobile/change-password', { oldPassword: oldPwd, newPassword: newPwd }, { headers: { Authorization: `Bearer ${token}` } });
      showMsg('Code PIN modifie', 'success'); setShowPwdChange(false); setOldPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (err: any) { showMsg(err.response?.data?.error || 'Erreur', 'error'); } setLoading(false);
  };

  // ============ RELEVE ============
  const saveReleve = () => {
    if (!selectedAbonne || !newIndex) return;
    const idx = parseInt(newIndex);
    if (isNaN(idx) || idx < 0) { showMsg('Index invalide', 'error'); return; }
    if (idx < selectedAbonne.lastindex) { showMsg(`Index >= ${selectedAbonne.lastindex}`, 'error'); return; }
    const b = getBilling();
    const ex = pendingReleves.find(r => r.abonne_id === selectedAbonne.id && r.mois === b.mois && r.annee === b.annee && !r.sent);
    if (ex && !window.confirm(`Remplacer le releve existant (${ex.newindex}) ?`)) return;
    const rel: PendingReleve = { abonne_id: selectedAbonne.id, mois: b.mois, annee: b.annee, newindex: idx, oldindex: selectedAbonne.lastindex,
      datenewindex: new Date().toISOString().split('T')[0], abonne_nom: selectedAbonne.prenomnom || `${selectedAbonne.prenom} ${selectedAbonne.nom}`, timestamp: Date.now(), sent: false };
    const up = [...pendingReleves.filter(r => !(r.abonne_id === selectedAbonne.id && r.mois === b.mois && r.annee === b.annee && !r.sent)), rel];
    setPendingReleves(up); store(SK.PENDING_RELEVES, up);
    const ua = abonnes.map(a => a.id === selectedAbonne.id ? { ...a, lastindex: idx, datelastindex: rel.datenewindex } : a);
    setAbonnes(ua); store(SK.ABONNES, ua);
    showMsg(`Index ${idx} enregistre`, 'success'); setNewIndex(''); setSelectedAbonne(null); setScreen('abonnes');
  };
  const deleteReleve = (r: PendingReleve) => {
    if (!window.confirm('Supprimer ?')) return;
    const up = pendingReleves.filter(x => x.timestamp !== r.timestamp); setPendingReleves(up); store(SK.PENDING_RELEVES, up);
    showMsg('Supprime', 'success'); setSelectedReleve(null); setScreen('releves');
  };
  const saveSignal = () => {
    if (!signalComment.trim() && signalMedia.length === 0) { showMsg('Ajoutez un commentaire ou un media', 'error'); return; }
    let loc = ''; if (navigator.geolocation) navigator.geolocation.getCurrentPosition(p => { loc = `${p.coords.latitude},${p.coords.longitude}`; }, () => {}, { timeout: 5000 });
    const up = [...pendingSignals, { type: signalType, comment: signalComment, location: loc, timestamp: Date.now(), media: signalMedia.length > 0 ? signalMedia : undefined }];
    setPendingSignals(up); store(SK.PENDING_SIGNALS, up); setSignalComment(''); setSignalMedia([]); showMsg('Signalement enregistre', 'success');
  };
  const handleMediaFile = (file: File, mediaType: 'image' | 'video') => {
    if (file.size > 10 * 1024 * 1024) { showMsg('Fichier trop volumineux (max 10 Mo)', 'error'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setSignalMedia(prev => [...prev, { type: mediaType, dataUrl: reader.result as string, name: file.name, size: file.size }]);
    };
    reader.readAsDataURL(file);
  };
  const startAudioRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onload = () => {
          setSignalMedia(prev => [...prev, { type: 'audio', dataUrl: reader.result as string, name: `audio_${Date.now()}.webm`, size: blob.size }]);
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach(t => t.stop());
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch { showMsg('Acces micro refuse', 'error'); }
  };
  const stopAudioRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };
  const removeMedia = (idx: number) => { setSignalMedia(prev => prev.filter((_, i) => i !== idx)); };

  // ============ QR SCANNER ============
  const stopScan = useCallback(async () => {
    if (scannerRef.current) { try { await scannerRef.current.stop(); } catch {} try { scannerRef.current.clear(); } catch {} scannerRef.current = null; }
    setScanning(false);
  }, []);
  const startScan = useCallback(async () => {
    setScanning(true); setScannedData(null); await new Promise(r => setTimeout(r, 300));
    if (!document.getElementById(scanDivId.current)) { showMsg('Erreur scanner', 'error'); setScanning(false); return; }
    try {
      const sc = new Html5Qrcode(scanDivId.current); scannerRef.current = sc;
      await sc.start({ facingMode: 'environment' }, { fps: 10, qrbox: { width: 250, height: 250 } },
        (txt) => {
          try { let d: any; try { d = JSON.parse(txt); } catch { d = { reference: txt }; }
            if (d.reference) { let m = 0, a = 0; if (d.periode) { const p = parseFrMonth(d.periode); if (p) { m = p.mois; a = p.annee; } }
              if (!m) { const b = getBilling(); m = b.mois; a = b.annee; }
              setScannedData({ reference: d.reference, montant: d.montantttc || d.montant || 0, abonne: d.abonne || '', periode: d.periode || `${MOIS[m]} ${a}`, mois: m, annee: a });
            } else showMsg('QR invalide', 'error');
          } catch { showMsg('Erreur QR', 'error'); } stopScan();
        }, () => {});
    } catch (err: any) { showMsg('Camera: ' + (err.message || err), 'error'); setScanning(false); }
  }, [stopScan]);
  useEffect(() => { return () => { if (scannerRef.current) try { scannerRef.current.stop(); } catch {} }; }, []);

  const savePaiement = () => {
    if (!scannedData?.reference) { showMsg('Scannez une facture', 'error'); return; }
    if (pendingPaiements.some(p => p.factureref === scannedData.reference)) { showMsg('Deja encaissee !', 'error'); return; }
    const p: PendingPaiement = { id: `${scannedData.reference}_${Date.now()}`, factureref: scannedData.reference, montant: scannedData.montant,
      mois: scannedData.mois, annee: scannedData.annee, periode: scannedData.periode, abonne: scannedData.abonne,
      datepaiement: new Date().toISOString().split('T')[0], moyenpaiement: payMethod, timestamp: Date.now(), sent: false };
    const up = [...pendingPaiements, p]; setPendingPaiements(up); store(SK.PENDING_PAIEMENTS, up);
    setScannedData(null); setPayMethod('espece'); showMsg(`${scannedData.reference} encaisse`, 'success');
  };

  // ============ COMPUTED ============
  const filtered = abonnes.filter(a => {
    if (selectedSecteur && a.secteur_id !== selectedSecteur) return false;
    if (searchTerm) { const s = searchTerm.toLowerCase(); return (a.prenomnom || `${a.prenom} ${a.nom}`).toLowerCase().includes(s) || (a.reference||'').includes(s) || (a.telephone||'').includes(s); }
    return true;
  });
  const getName = (a: Abonne) => (a.prenomnom?.trim() || `${a.prenom||''} ${a.nom||''}`.trim()) || a.reference;
  const hasPending = (id: string) => pendingReleves.some(r => r.abonne_id === id && !r.sent);
  const hasServer = (id: string) => !!relevesStatus[id];
  const billing = getBilling();
  const uR = pendingReleves.filter(r => !r.sent);
  const uP = pendingPaiements.filter(p => !p.sent);
  const total = uR.length + uP.length + pendingSignals.length;

  // ============ SHARED UI ============
  const DAYS = ['Dimanche','Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'];
  const MONTHS_FR = ['Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];
  const now = new Date();
  const dateStr = `${DAYS[now.getDay()]} ${now.getDate()} ${MONTHS_FR[now.getMonth()]}`;

  const showNav = !['login','releve','releve-detail','encaisser','upload','upload-ok'].includes(screen);
  const activeTab = (() => {
    if (screen === 'home') return 'home';
    if (['secteurs','abonnes','releves','localites'].includes(screen)) return 'collecte';
    if (screen === 'paiements') return 'paiements';
    if (['gestionnaire'].includes(screen)) return 'profil';
    return '';
  })();

  const Header = FgHeader;
  const Input = FgInput;
  const Btn = FgBtn;
  const Badge = FgBadge;

  // Stable nav callbacks
  const navOnScan = useCallback(() => { setScannedData(null); go('encaisser'); }, []);
  const navOnHome = useCallback(() => go('home'), []);
  const navOnCollecte = useCallback(() => { setSelectedSecteur(''); setSearchTerm(''); go('secteurs'); }, []);
  const navOnPaiements = useCallback(() => go('paiements'), []);
  const navOnProfil = useCallback(() => go('gestionnaire'), []);
  const pageWrapProps = { showNav, activeTab, msg, loading, onScan: navOnScan, onHome: navOnHome, onCollecte: navOnCollecte, onPaiements: navOnPaiements, onProfil: navOnProfil };

  // ==================== LOGIN ====================
  if (screen === 'login') {
    return (
      <div style={{ minHeight: '100vh', background: '#FFFFFF', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 28 }}>
        <div style={{ width: '100%', maxWidth: 360 }}>
          <div style={{ textAlign: 'center', marginBottom: 36, animation: 'fg-scale 0.5s ease' }}>
            <img src={logoFull} alt="FORGEST" style={{ width: '70%', maxWidth: 260, marginBottom: 8 }} />
            <div style={{ color: C.textMuted, fontSize: 12, letterSpacing: 1 }}>Gestion des Forages</div>
          </div>
          <div style={{ ...card, padding: 28, animation: 'fg-slide-up 0.4s ease' }}>
            {msg.text && <div style={{ padding: '10px 14px', borderRadius: 10, marginBottom: 16, fontSize: 12, fontWeight: 500,
              background: msg.type === 'error' ? '#FEF2F2' : '#F0FDF4', color: msg.type === 'error' ? C.error : C.success,
              border: `1px solid ${msg.type === 'error' ? '#FECACA' : '#BBF7D0'}` }}>{msg.text}</div>}
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, display: 'block' }}>Telephone</label>
                <Input type="tel" inputMode="tel" placeholder="7X XXX XX XX" value={loginPhone} onChange={e => setLoginPhone(e.target.value)} required />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10, display: 'block', textAlign: 'center' }}>Code PIN</label>
                <PinInput value={loginPwd} onChange={setLoginPwd} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, cursor: 'pointer' }}>
                <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${remember ? C.primary : '#D1D5DB'}`, background: remember ? C.primary : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }} onClick={() => setRemember(!remember)}>
                  {remember && <span style={{ color: C.white, fontSize: 12 }}>{'\u2713'}</span>}
                </div>
                <span style={{ fontSize: 13, color: C.textSec }}>Rester connecte</span>
              </label>
              <Btn label={loading ? 'Connexion...' : 'Se connecter'} full bg={C.gradGreen} disabled={loading || loginPwd.length < 4} />
            </form>
          </div>
          {!isOnline && <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: C.warning, animation: 'fg-pulse 2s infinite' }}>{'\u26A0'} Hors ligne</div>}
          <p style={{ textAlign: 'center', fontSize: 10, color: C.textMuted, marginTop: 28, letterSpacing: 1 }}>WINLAYTECH {'\u00B7'} FORGEST v2.0</p>
        </div>
        <FgLoader loading={loading} /><FgToast msg={msg} showNav={showNav} />
      </div>
    );
  }

  // ==================== HOME ====================
  if (screen === 'home') {
    return (
      <FgPageWrap {...pageWrapProps}>
        <FgOffline isOnline={isOnline} />
        {/* Green header */}
        <div style={{ background: C.primaryDark, padding: '20px 20px 24px', borderRadius: '0 0 24px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <img src="/icon.png" alt="" style={{ width: 32, height: 32 }} />
              </div>
              <div>
                <div style={{ color: C.white, fontSize: 16, fontWeight: 700, letterSpacing: 0.5 }}>FORGEST</div>
                <div style={{ color: C.accentLight, fontSize: 11 }}>{partenaire?.libelle || user?.partenaire_nom}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {isOnline ? <span style={{ fontSize: 10, color: C.accentLight, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.accent }} />En ligne</span>
                : <span style={{ fontSize: 10, color: '#FBBF24', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#FBBF24' }} />Hors-ligne</span>}
            </div>
          </div>
        </div>

        {/* Greeting */}
        <div style={{ padding: '20px 20px 12px' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>Bonjour, {user?.prenom}</div>
          <div style={{ fontSize: 13, color: C.textMuted, marginTop: 2 }}>{dateStr}</div>
        </div>

        {/* Stats cards */}
        <div style={{ padding: '0 20px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <button onClick={() => { setSelectedSecteur(''); setSearchTerm(''); go('secteurs'); }} className="fg-card"
            style={{ background: C.gradGreen, borderRadius: 16, padding: '18px 16px', color: C.white, animation: 'fg-slide-up 0.3s ease', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'transform 0.15s' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, opacity: 0.9 }}>
              <span style={{ fontSize: 14 }}>{'\u{1F465}'}</span><span style={{ fontSize: 12 }}>Abonnes</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800 }}>{abonnes.length}</div>
            <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>{'\u{1F4CD}'} {secteurs.length} secteurs</div>
          </button>
          <button onClick={() => go('releves')} className="fg-card"
            style={{ background: C.gradTeal, borderRadius: 16, padding: '18px 16px', color: C.white, animation: 'fg-slide-up 0.3s ease 0.05s both', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'transform 0.15s' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, opacity: 0.9 }}>
              <span style={{ fontSize: 14 }}>{'\u{1F4CB}'}</span><span style={{ fontSize: 12 }}>Releves</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800 }}>{uR.length}</div>
            <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>{'\u{1F4C5}'} {billing.label}</div>
          </button>
        </div>

        {/* Navigation tiles - colored left border style */}
        <div style={{ padding: '0 20px 16px' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 14 }}>{'\u{1F4CC}'}</span> Explorer
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { label: 'Secteurs', desc: `${secteurs.length} zones`, icon: '\u{1F4CD}', gradient: 'linear-gradient(135deg, #2196F3, #64B5F6)', action: () => { setSelectedSecteur(''); setSearchTerm(''); go('secteurs'); } },
              { label: 'Localites', desc: `${localites.length} villages`, icon: '\u{1F3D8}\uFE0F', gradient: 'linear-gradient(135deg, #059669, #34D399)', action: () => go('localites') },
              { label: 'Paiements', desc: `${uP.length} en attente`, icon: '\u{1F4B3}', gradient: 'linear-gradient(135deg, #E8943A, #FBBF24)', action: () => go('paiements') },
              { label: 'Signalisations', desc: `${pendingSignals.length} signalement(s)`, icon: '\u26A0\uFE0F', gradient: 'linear-gradient(135deg, #EF4444, #F87171)', action: () => go('signalisations') },
            ].map((tile, i) => (
              <button key={i} onClick={tile.action} className="fg-card"
                style={{ background: C.white, border: 'none', borderRadius: 14, padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'stretch',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)', transition: 'transform 0.15s', animation: `fg-slide-up 0.3s ease ${0.04 * i}s both`, overflow: 'hidden' }}>
                <div style={{ width: 5, background: tile.gradient, flexShrink: 0 }} />
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '14px 16px', gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: tile.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
                    <span style={{ filter: 'brightness(1.1)' }}>{tile.icon}</span>
                  </div>
                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{tile.label}</div>
                    <div style={{ fontSize: 11, color: C.textSec, marginTop: 2 }}>{tile.desc}</div>
                  </div>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Quick actions - pill buttons */}
        <div style={{ padding: '0 20px 16px' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 14 }}>{'\u26A1'}</span> Actions rapides
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => { if (!isOnline) { showMsg('Connexion requise', 'error'); return; } handleSync(); }} className="fg-btn"
              style={{ flex: 1, padding: '14px 10px', background: 'linear-gradient(135deg, #2196F3, #64B5F6)', border: 'none', borderRadius: 14, cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, boxShadow: '0 3px 12px rgba(33,150,243,0.25)', transition: 'transform 0.15s' }}>
              <span style={{ fontSize: 22 }}>{'\u{1F504}'}</span>
              <span style={{ color: C.white, fontSize: 12, fontWeight: 700 }}>Charger</span>
            </button>
            <button onClick={() => go('upload')} className="fg-btn"
              style={{ flex: 1, padding: '14px 10px', background: 'linear-gradient(135deg, #E8943A, #FBBF24)', border: 'none', borderRadius: 14, cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, position: 'relative', boxShadow: '0 3px 12px rgba(232,148,58,0.25)', transition: 'transform 0.15s' }}>
              <span style={{ fontSize: 22 }}>{'\u{1F4E4}'}</span>
              <span style={{ color: C.white, fontSize: 12, fontWeight: 700 }}>Envoyer</span>
              {total > 0 && <Badge n={total} />}
            </button>
            <button onClick={() => go('signalisations')} className="fg-btn"
              style={{ flex: 1, padding: '14px 10px', background: 'linear-gradient(135deg, #EF4444, #F87171)', border: 'none', borderRadius: 14, cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, position: 'relative', boxShadow: '0 3px 12px rgba(239,68,68,0.25)', transition: 'transform 0.15s' }}>
              <span style={{ fontSize: 22 }}>{'\u26A0\uFE0F'}</span>
              <span style={{ color: C.white, fontSize: 12, fontWeight: 700 }}>Signaler</span>
              {pendingSignals.length > 0 && <Badge n={pendingSignals.length} />}
            </button>
          </div>
        </div>

        {/* Pending uploads banner */}
        {total > 0 && (
          <div style={{ padding: '0 20px 16px' }}>
            <div style={{ ...card, padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: `1px solid ${C.accent}40`, background: '#FFFBF5' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{'\u{1F4E4}'}</div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{total} donnee(s) a envoyer</div>
                  <div style={{ fontSize: 10, color: C.textMuted }}>{uR.length} releves, {uP.length} paiements</div>
                </div>
              </div>
              <button onClick={() => go('upload')} className="fg-btn" style={{ background: C.accent, border: 'none', color: C.white, padding: '7px 14px', borderRadius: 10, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Envoyer</button>
            </div>
          </div>
        )}

        {/* Sync + logout */}
        <div style={{ padding: '0 20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {syncDate && <span style={{ fontSize: 11, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}>{'\u{1F504}'} {new Date(syncDate).toLocaleString('fr-FR')}</span>}
          <button onClick={handleLogout} style={{ background: 'none', border: 'none', fontSize: 12, color: C.rose, fontWeight: 600, cursor: 'pointer' }}>
            {'\u{1F6AA}'} Deconnexion</button>
        </div>
      </FgPageWrap>
    );
  }

  // ==================== GESTIONNAIRE ====================
  if (screen === 'gestionnaire') {
    return (
      <FgPageWrap {...pageWrapProps}>
        <Header title="Mon profil" onBack={() => go('home')} green />
        <div style={{ padding: 20 }}>
          <div style={{ ...card, padding: 24, marginBottom: 16, textAlign: 'center', animation: 'fg-slide-up 0.3s ease' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: C.gradGreen, margin: '0 auto 14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 32, color: C.white, boxShadow: '0 4px 16px rgba(33,150,243,0.2)' }}>{'\u{1F464}'}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{user?.prenom} {user?.nom}</div>
            <div style={{ fontSize: 12, color: C.primary, marginTop: 4, fontWeight: 500 }}>{user?.role}</div>
          </div>
          <div style={{ ...card, padding: 20, marginBottom: 16, animation: 'fg-slide-up 0.3s ease 0.05s both' }}>
            {[['Telephone', user?.telephone, C.primary], ['Email', user?.email, C.teal], ['Partenaire', partenaire?.libelle || user?.partenaire_nom, C.success]].map(([l, v, c]) => (
              <div key={l as string} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 13, color: C.textMuted }}>{l as string}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: c as string, maxWidth: '60%', textAlign: 'right', wordBreak: 'break-all' }}>{(v as string) || '-'}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16, animation: 'fg-slide-up 0.3s ease 0.1s both' }}>
            {[[abonnes.length, 'Abonnes', C.primary], [uR.length, 'Releves', C.teal], [uP.length, 'Paiements', C.warning]].map(([v, l, c]) => (
              <div key={l as string} style={{ ...card, padding: 14, textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: c as string }}>{v as number}</div>
                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>{l as string}</div>
              </div>
            ))}
          </div>
          <button onClick={() => setShowPwdChange(!showPwdChange)} className="fg-card" style={{ ...card, width: '100%', padding: '14px 18px', cursor: 'pointer', marginBottom: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ color: C.textSec, fontSize: 13, fontWeight: 500 }}>{'\u{1F511}'} Changer code PIN</span>
            <span style={{ color: C.textMuted, fontSize: 12 }}>{showPwdChange ? '\u25B2' : '\u25BC'}</span>
          </button>
          {showPwdChange && (
            <div style={{ ...card, padding: 18, marginBottom: 16, animation: 'fg-slide-up 0.2s ease' }}>
              <div style={{ fontSize: 12, color: C.textSec, marginBottom: 8, fontWeight: 600 }}>Ancien code PIN</div>
              <PinInput value={oldPwd} onChange={setOldPwd} />
              <div style={{ fontSize: 12, color: C.textSec, marginBottom: 8, marginTop: 14, fontWeight: 600 }}>Nouveau code PIN</div>
              <PinInput value={newPwd} onChange={setNewPwd} />
              <div style={{ fontSize: 12, color: C.textSec, marginBottom: 8, marginTop: 14, fontWeight: 600 }}>Confirmer</div>
              <PinInput value={confirmPwd} onChange={setConfirmPwd} />
              <div style={{ marginTop: 16 }}>
                <Btn label={loading ? 'Modification...' : 'Modifier'} full disabled={loading || newPwd.length < 4} onClick={handleChangePwd} />
              </div>
            </div>
          )}
          <div style={{ ...card, padding: 16, textAlign: 'center', fontSize: 11, color: C.textMuted }}>
            <div style={{ fontWeight: 600, color: C.textSec, marginBottom: 4 }}>Support</div>
            WINLAYTECH {'\u00B7'} <span style={{ color: C.primary }}>contact@winlaytech.com</span>
          </div>
        </div>
      </FgPageWrap>
    );
  }

  // ==================== LOCALITES ====================
  if (screen === 'localites') {
    return (
      <FgPageWrap {...pageWrapProps}>
        <Header title="Localites" onBack={() => go('home')} green />
        <div style={{ padding: '16px 20px' }}>
          <div style={{ padding: '10px 0', marginBottom: 8, fontSize: 13, fontWeight: 600, color: C.textSec }}>{localites.length} localites</div>
          {localites.map((l, i) => (
            <div key={l.id} style={{ ...card, padding: '14px 16px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, animation: `fg-slide-up 0.2s ease ${i * 0.02}s both` }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{'\u{1F4CD}'}</div>
              <span style={{ fontSize: 14, fontWeight: 500, color: C.text }}>{l.libelle}</span>
            </div>
          ))}
          {!localites.length && <p style={{ textAlign: 'center', padding: 40, color: C.textMuted, fontSize: 13 }}>Appuyez sur CHARGER</p>}
        </div>
      </FgPageWrap>
    );
  }

  // ==================== SECTEURS ====================
  if (screen === 'secteurs') {
    return (
      <FgPageWrap {...pageWrapProps}>
        <Header title="Secteurs" onBack={() => go('home')} green />
        <div style={{ padding: '16px 20px' }}>
          <div style={{ padding: '10px 0', marginBottom: 8, fontSize: 13, fontWeight: 600, color: C.textSec }}>{secteurs.length} secteurs</div>
          {secteurs.map((s, i) => (
            <button key={s.id} onClick={() => { setSelectedSecteur(s.id); setSearchTerm(''); go('abonnes'); }} className="fg-card"
              style={{ ...card, width: '100%', padding: '14px 16px', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                cursor: 'pointer', transition: 'transform 0.15s', animation: `fg-slide-up 0.2s ease ${i * 0.02}s both` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{'\u{1F4CD}'}</div>
                <div style={{ textAlign: 'left' }}><div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{s.libelle}</div>
                  {s.forage && <div style={{ fontSize: 11, color: C.textMuted }}>{s.forage}</div>}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.primary }}>{abonnes.filter(a => a.secteur_id === s.id).length}</span>
                <span style={{ color: C.textMuted, fontSize: 16 }}>{'\u203A'}</span>
              </div>
            </button>
          ))}
          {!secteurs.length && <p style={{ textAlign: 'center', padding: 40, color: C.textMuted, fontSize: 13 }}>Appuyez sur CHARGER</p>}
        </div>
      </FgPageWrap>
    );
  }

  // ==================== ABONNES ====================
  if (screen === 'abonnes') {
    const secLabel = selectedSecteur ? secteurs.find(s => s.id === selectedSecteur)?.libelle : 'Tous';
    return (
      <FgPageWrap {...pageWrapProps}>
        <Header title={secLabel || 'Abonnes'} onBack={() => { setSelectedSecteur(''); go('secteurs'); }} green />
        <div style={{ padding: '12px 20px' }}>
          <Input type="text" placeholder="Rechercher un abonne..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ marginBottom: 10 }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: C.textMuted }}>{filtered.length} abonnes</span>
            {selectedSecteur && <button onClick={() => setSelectedSecteur('')} style={{ fontSize: 11, color: C.primary, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Voir tous</button>}
          </div>
          {filtered.map((a) => {
            const done = hasPending(a.id) || hasServer(a.id);
            return (
              <button key={a.id} onClick={() => { setSelectedAbonne(a); setNewIndex(''); go('releve'); }} className="fg-card"
                style={{ ...card, width: '100%', padding: '12px 14px', marginBottom: 6, display: 'flex', alignItems: 'center',
                  cursor: 'pointer', transition: 'transform 0.15s', borderLeft: done ? `3px solid ${C.success}` : `3px solid transparent` }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: done ? '#ECFDF5' : C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, marginRight: 12, flexShrink: 0, color: done ? C.success : C.textMuted }}>{done ? '\u2713' : '\u{1F464}'}</div>
                <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getName(a)} <span style={{ fontWeight: 400, color: C.textMuted, fontSize: 11 }}>{a.categorie || ''}</span></div>
                  <div style={{ fontSize: 11, color: C.primary, fontWeight: 500 }}>{a.secteur || a.localite}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{a.lastindex}</div>
                  <div style={{ fontSize: 9, color: C.textMuted }}>index</div>
                </div>
              </button>
            );
          })}
          {!filtered.length && <p style={{ textAlign: 'center', padding: 40, color: C.textMuted }}>{abonnes.length ? 'Aucun resultat' : 'CHARGER les donnees'}</p>}
        </div>
      </FgPageWrap>
    );
  }

  // ==================== RELEVE (saisie) ====================
  if (screen === 'releve' && selectedAbonne) {
    const conso = newIndex ? Math.max(0, parseInt(newIndex) - selectedAbonne.lastindex) : 0;
    const ex = pendingReleves.find(r => r.abonne_id === selectedAbonne.id && r.mois === billing.mois && r.annee === billing.annee && !r.sent);
    return (
      <div style={{ minHeight: '100vh', background: C.bg }}>
        <Header title="Nouveau releve" onBack={() => { setSelectedAbonne(null); go('abonnes'); }} green />
        <div style={{ padding: 20 }}>
          <div style={{ textAlign: 'center', marginBottom: 14 }}>
            <span style={{ display: 'inline-block', padding: '6px 18px', fontSize: 12, fontWeight: 600, color: C.white, background: C.gradGreen, borderRadius: 20 }}>{billing.label}</span>
          </div>
          <div style={{ ...card, padding: 20, marginBottom: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>{getName(selectedAbonne)}</div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 14 }}>Ref: {selectedAbonne.reference} {'\u00B7'} {selectedAbonne.categorie} {'\u00B7'} {selectedAbonne.secteur}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ background: '#ECFDF5', borderRadius: 12, padding: 14, textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4 }}>Ancien index</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: C.primary }}>{selectedAbonne.lastindex}</div>
              </div>
              <div style={{ background: selectedAbonne.resteapayer > 0 ? '#FEF2F2' : '#ECFDF5', borderRadius: 12, padding: 14, textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4 }}>Reste a payer</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: selectedAbonne.resteapayer > 0 ? C.error : C.success }}>
                  {(selectedAbonne.resteapayer || 0).toLocaleString('fr-FR')} F</div>
              </div>
            </div>
          </div>
          {ex && <div style={{ ...card, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#92400E', background: '#FEF3C7', border: '1px solid #FDE68A' }}>
            {'\u26A0'} Existant: {ex.newindex}. Sera remplace.</div>}
          <div style={{ ...card, padding: 20, marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, display: 'block' }}>Nouvel index</label>
            <input type="number" value={newIndex} onChange={e => setNewIndex(e.target.value)} placeholder={`>= ${selectedAbonne.lastindex}`} autoFocus
              className="fg-input" style={{ width: '100%', padding: 18, fontSize: 32, fontWeight: 800, textAlign: 'center', background: C.surface,
                border: `2px solid ${C.border}`, borderRadius: 14, color: C.text, outline: 'none', transition: 'all 0.2s' }} />
            {newIndex && <div style={{ marginTop: 14, textAlign: 'center' }}>
              <span style={{ fontSize: 12, color: C.textMuted }}>Consommation </span>
              <span style={{ fontSize: 22, fontWeight: 800, color: conso > 0 ? C.teal : C.textMuted }}>{conso} m3</span>
            </div>}
          </div>
          <Btn label="Enregistrer" full bg={C.gradGreen} disabled={!newIndex} onClick={saveReleve} icon={'\u2713'} />
        </div><FgToast msg={msg} showNav={showNav} /><FgLoader loading={loading} />
      </div>
    );
  }

  // ==================== RELEVES (liste) ====================
  if (screen === 'releves') {
    const fl = pendingReleves.filter(r => !searchTerm || r.abonne_nom.toLowerCase().includes(searchTerm.toLowerCase())).sort((a, b) => b.timestamp - a.timestamp);
    return (
      <FgPageWrap {...pageWrapProps}>
        <Header title="Releves" onBack={() => go('home')} green />
        <div style={{ padding: '16px 20px' }}>
          <div style={{ padding: '10px 0', marginBottom: 8, fontSize: 13, fontWeight: 600, color: C.textSec }}>
            {pendingReleves.length} releve(s) {'\u00B7'} {billing.label}</div>
          <Input placeholder="Rechercher..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ marginBottom: 12 }} />
          {fl.map((r, i) => (
            <button key={i} onClick={() => { setSelectedReleve(r); go('releve-detail'); }} className="fg-card"
              style={{ ...card, width: '100%', padding: '12px 14px', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                cursor: 'pointer', transition: 'transform 0.15s', borderLeft: `3px solid ${r.sent ? C.success : C.warning}` }}>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{r.abonne_nom}</div>
                <div style={{ fontSize: 11, color: C.textMuted }}>{r.oldindex} {'\u2192'} {r.newindex} ({r.newindex - r.oldindex} m3)</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: r.sent ? C.success : C.warning }}>{r.sent ? '\u2713 Envoye' : 'En attente'}</div>
                <div style={{ fontSize: 9, color: C.textMuted }}>{new Date(r.timestamp).toLocaleString('fr-FR')}</div>
              </div>
            </button>
          ))}
          {!fl.length && <p style={{ textAlign: 'center', padding: 40, color: C.textMuted }}>Aucun releve</p>}
        </div>
      </FgPageWrap>
    );
  }

  // ==================== RELEVE DETAIL ====================
  if (screen === 'releve-detail' && selectedReleve) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg }}>
        <Header title="Detail" onBack={() => { setSelectedReleve(null); go('releves'); }} green />
        <div style={{ padding: 20 }}>
          <div style={{ ...card, padding: 24, animation: 'fg-scale 0.3s ease' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 18 }}>{selectedReleve.abonne_nom}</div>
            {[['Mois', `${selectedReleve.mois}/${selectedReleve.annee}`], ['Ancien', selectedReleve.oldindex], ['Nouveau', selectedReleve.newindex],
              ['Conso', `${selectedReleve.newindex - selectedReleve.oldindex} m3`], ['Date', new Date(selectedReleve.timestamp).toLocaleString('fr-FR')],
              ['Statut', selectedReleve.sent ? 'Envoye' : 'En attente']].map(([l, v]) => (
              <div key={l as string} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 13, color: C.textMuted }}>{l}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: l === 'Statut' ? (selectedReleve.sent ? C.success : C.warning) : C.text }}>{v}</span>
              </div>))}
            {!selectedReleve.sent && <div style={{ marginTop: 20 }}>
              <Btn label="Supprimer" full bg={C.gradRose} onClick={() => deleteReleve(selectedReleve)} icon={'\u{1F5D1}'} /></div>}
          </div>
        </div><FgToast msg={msg} showNav={showNav} />
      </div>
    );
  }

  // ==================== ENCAISSER ====================
  if (screen === 'encaisser') {
    return (
      <div style={{ minHeight: '100vh', background: C.bg }}>
        <Header title="Encaisser" onBack={() => { stopScan(); setScannedData(null); go('paiements'); }} green />
        <div style={{ padding: 20 }}>
          {!scannedData ? (
            <div style={{ ...card, padding: 28, textAlign: 'center', animation: 'fg-scale 0.3s ease' }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#ECFDF5', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}>{'\u{1F4F7}'}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6 }}>Scanner la facture</div>
              <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 24 }}>Pointez le QR code de la facture</div>
              {scanning ? (<>
                <div id={scanDivId.current} style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 16 }} />
                <Btn label="Arreter" full bg={C.gradRose} onClick={stopScan} />
              </>) : (
                <Btn label="Scanner" full bg={C.gradGreen} onClick={startScan} icon={'\u{1F4F8}'} />
              )}
            </div>
          ) : (
            <div style={{ ...card, padding: 24, animation: 'fg-scale 0.3s ease' }}>
              <div style={{ textAlign: 'center', marginBottom: 18 }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#ECFDF5', margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>{'\u2705'}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Facture detectee</div>
              </div>
              {[['Reference', scannedData.reference], ['Abonne', scannedData.abonne || '-'], ['Montant', `${(scannedData.montant || 0).toLocaleString('fr-FR')} F`], ['Periode', scannedData.periode]].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ fontSize: 13, color: C.textMuted }}>{l}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{v}</span>
                </div>))}
              <div style={{ marginTop: 16 }}>
                <label style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, marginBottom: 6, display: 'block' }}>Moyen de paiement</label>
                <select value={payMethod} onChange={e => setPayMethod(e.target.value)}
                  style={{ width: '100%', padding: 14, borderRadius: 12, border: `1px solid ${C.border}`, background: C.white, color: C.text, fontSize: 14 }}>
                  <option value="espece">Especes</option><option value="app_om">Orange Money</option><option value="app_wave">Wave</option>
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 20 }}>
                <Btn label="Annuler" onClick={() => setScannedData(null)} bg={C.surface} color={C.textSec} />
                <Btn label="Encaisser" onClick={savePaiement} bg={C.gradGreen} icon={'\u2713'} />
              </div>
            </div>
          )}
        </div><FgToast msg={msg} showNav={showNav} /><FgLoader loading={loading} />
      </div>
    );
  }

  // ==================== PAIEMENTS ====================
  if (screen === 'paiements') {
    const fp = pendingPaiements.filter(p => {
      if (pFilterMois && p.mois !== pFilterMois) return false;
      if (pFilterAnnee && p.annee !== pFilterAnnee) return false;
      if (pSearch) return p.abonne.toLowerCase().includes(pSearch.toLowerCase()) || p.factureref.includes(pSearch);
      return true;
    }).sort((a, b) => b.timestamp - a.timestamp);
    const tot = fp.reduce((s, p) => s + p.montant, 0);
    return (
      <FgPageWrap {...pageWrapProps}>
        <Header title="Paiements" onBack={() => go('home')} green />
        <div style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <select value={pFilterMois} onChange={e => setPFilterMois(Number(e.target.value))}
              style={{ flex: 1, padding: 12, borderRadius: 12, border: `1px solid ${C.border}`, background: C.white, color: C.text, fontSize: 13 }}>
              <option value={0}>Tous</option>{MOIS.slice(1).map((m, i) => <option key={i} value={i + 1}>{m}</option>)}</select>
            <select value={pFilterAnnee} onChange={e => setPFilterAnnee(Number(e.target.value))}
              style={{ width: 80, padding: 12, borderRadius: 12, border: `1px solid ${C.border}`, background: C.white, color: C.text, fontSize: 13 }}>
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}</select>
          </div>
          <Input placeholder="Rechercher..." value={pSearch} onChange={e => setPSearch(e.target.value)} style={{ marginBottom: 10 }} />
          <div style={{ ...card, padding: '10px 16px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600 }}>
            <span style={{ color: C.textSec }}>{fp.length} paiement(s)</span>
            <span style={{ color: C.success }}>{tot.toLocaleString('fr-FR')} F</span>
          </div>
          <div style={{ marginBottom: 16 }}>
            <Btn label="Encaisser" full bg={C.gradGreen} onClick={() => go('encaisser')} icon={'\u{1F4F8}'} />
          </div>
          {fp.map((p, i) => (
            <div key={i} style={{ ...card, padding: '12px 14px', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              borderLeft: `3px solid ${p.sent ? C.success : C.warning}` }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{p.abonne || p.factureref}</div>
                <div style={{ fontSize: 11, color: C.textMuted }}>{p.factureref} {'\u00B7'} {p.periode}</div>
                <div style={{ fontSize: 11, color: C.textMuted }}>{p.datepaiement} {'\u00B7'} {p.moyenpaiement === 'espece' ? 'Especes' : p.moyenpaiement === 'app_om' ? 'OM' : 'Wave'}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: C.success }}>{p.montant.toLocaleString('fr-FR')} F</div>
                <div style={{ fontSize: 9, fontWeight: 600, color: p.sent ? C.success : C.warning }}>{p.sent ? '\u2713 Envoye' : 'Attente'}</div>
              </div>
            </div>
          ))}
          {!fp.length && <p style={{ textAlign: 'center', padding: 40, color: C.textMuted }}>Aucun paiement</p>}
        </div>
      </FgPageWrap>
    );
  }

  // ==================== SIGNALISATIONS ====================
  if (screen === 'signalisations') {
    const typeOptions = [
      { value: 'incident', label: 'Incident', icon: '\u{1F6A8}', color: '#EF4444' },
      { value: 'panne', label: 'Panne', icon: '\u{1F527}', color: '#F59E0B' },
      { value: 'fuite', label: 'Fuite d\'eau', icon: '\u{1F4A7}', color: '#3B82F6' },
      { value: 'compteur', label: 'Compteur', icon: '\u{1F50C}', color: '#8B5CF6' },
      { value: 'autre', label: 'Autre', icon: '\u{1F4DD}', color: '#6B7280' },
    ];
    return (
      <FgPageWrap {...pageWrapProps}>
        <Header title="Signaler une anomalie" onBack={() => go('home')} green />
        <div style={{ padding: 20 }}>
          {/* Type selection - chips */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.textSec, marginBottom: 10 }}>Type de signalement</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {typeOptions.map(opt => (
                <button key={opt.value} onClick={() => setSignalType(opt.value)}
                  style={{ padding: '8px 14px', borderRadius: 20, border: signalType === opt.value ? `2px solid ${opt.color}` : `1px solid ${C.border}`,
                    background: signalType === opt.value ? `${opt.color}12` : C.white, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                    transition: 'all 0.2s', fontSize: 13, fontWeight: signalType === opt.value ? 700 : 500,
                    color: signalType === opt.value ? opt.color : C.textSec }}>
                  <span style={{ fontSize: 15 }}>{opt.icon}</span>{opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.textSec, marginBottom: 8 }}>Description</div>
            <textarea value={signalComment} onChange={e => setSignalComment(e.target.value)} placeholder="Decrivez l'anomalie ou la remontee terrain..."
              rows={4} className="fg-input" style={{ width: '100%', padding: 14, borderRadius: 14, border: `1px solid ${C.border}`, background: C.white,
                color: C.text, fontSize: 14, resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
          </div>

          {/* Media capture buttons */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.textSec, marginBottom: 10 }}>Pieces jointes</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              {/* Photo */}
              <button onClick={() => fileInputRef.current?.click()} className="fg-card"
                style={{ ...card, padding: '14px 8px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, transition: 'transform 0.15s' }}>
                <div style={{ width: 44, height: 44, borderRadius: 14, background: 'linear-gradient(135deg, #3B82F6, #60A5FA)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 10px rgba(59,130,246,0.3)' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                </div>
                <span style={{ color: '#3B82F6', fontSize: 11, fontWeight: 700 }}>Photo</span>
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) handleMediaFile(f, 'image'); e.target.value = ''; }} />

              {/* Video */}
              <button onClick={() => videoInputRef.current?.click()} className="fg-card"
                style={{ ...card, padding: '14px 8px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, transition: 'transform 0.15s' }}>
                <div style={{ width: 44, height: 44, borderRadius: 14, background: 'linear-gradient(135deg, #8B5CF6, #A78BFA)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 10px rgba(139,92,246,0.3)' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                </div>
                <span style={{ color: '#8B5CF6', fontSize: 11, fontWeight: 700 }}>Video</span>
              </button>
              <input ref={videoInputRef} type="file" accept="video/*" capture="environment" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) handleMediaFile(f, 'video'); e.target.value = ''; }} />

              {/* Audio */}
              <button onClick={isRecording ? stopAudioRecording : startAudioRecording} className="fg-card"
                style={{ ...card, padding: '14px 8px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, transition: 'transform 0.15s',
                  border: isRecording ? '2px solid #EF4444' : `1px solid ${C.border}` }}>
                <div style={{ width: 44, height: 44, borderRadius: 14,
                  background: isRecording ? 'linear-gradient(135deg, #EF4444, #F87171)' : 'linear-gradient(135deg, #10B981, #34D399)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: isRecording ? '0 3px 10px rgba(239,68,68,0.3)' : '0 3px 10px rgba(16,185,129,0.3)',
                  animation: isRecording ? 'fg-pulse 1s infinite' : 'none' }}>
                  {isRecording
                    ? <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
                    : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                  }
                </div>
                <span style={{ color: isRecording ? '#EF4444' : '#10B981', fontSize: 11, fontWeight: 700 }}>
                  {isRecording ? 'Arreter' : 'Audio'}
                </span>
              </button>
            </div>
          </div>

          {/* Media preview */}
          {signalMedia.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.textSec, marginBottom: 8 }}>{signalMedia.length} fichier(s) joint(s)</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {signalMedia.map((m, i) => (
                  <div key={i} style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.border}`, background: C.surface }}>
                    {m.type === 'image' ? (
                      <img src={m.dataUrl} alt="" style={{ width: 80, height: 80, objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <div style={{ width: 80, height: 80, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        <span style={{ fontSize: 24 }}>{m.type === 'video' ? '\u{1F3AC}' : '\u{1F3A4}'}</span>
                        <span style={{ fontSize: 9, color: C.textMuted }}>{(m.size / 1024).toFixed(0)} Ko</span>
                      </div>
                    )}
                    <button onClick={() => removeMedia(i)} style={{ position: 'absolute', top: 2, right: 2, width: 20, height: 20, borderRadius: '50%',
                      background: 'rgba(239,68,68,0.9)', color: 'white', border: 'none', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      lineHeight: 1 }}>{'\u00D7'}</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Submit */}
          <Btn label="Enregistrer le signalement" full bg={C.gradOrange} onClick={saveSignal} icon={'\u{1F4E8}'} />

          {/* Pending signals list */}
          {pendingSignals.length > 0 && (
            <div style={{ ...card, padding: 18, marginTop: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>En attente d'envoi ({pendingSignals.length})</div>
              {pendingSignals.map((s, i) => (
                <div key={i} style={{ padding: '12px 0', borderBottom: i < pendingSignals.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 14 }}>{typeOptions.find(t => t.value === s.type)?.icon || '\u{1F4DD}'}</span>
                      <span style={{ fontWeight: 700, color: typeOptions.find(t => t.value === s.type)?.color || C.text, fontSize: 13, textTransform: 'capitalize' }}>{s.type}</span>
                    </div>
                    <span style={{ color: C.textMuted, fontSize: 10 }}>{new Date(s.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  {s.comment && <div style={{ color: C.textSec, fontSize: 12, marginTop: 2 }}>{s.comment}</div>}
                  {s.media && s.media.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      {s.media.map((m, j) => (
                        <div key={j} style={{ padding: '3px 8px', borderRadius: 8, background: C.surface, fontSize: 10, color: C.textSec, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span>{m.type === 'image' ? '\u{1F4F7}' : m.type === 'video' ? '\u{1F3AC}' : '\u{1F3A4}'}</span>
                          {m.type === 'image' ? 'Photo' : m.type === 'video' ? 'Video' : 'Audio'}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </FgPageWrap>
    );
  }

  // ==================== UPLOAD ====================
  if (screen === 'upload') {
    return (
      <div style={{ minHeight: '100vh', background: C.bg }}>
        <Header title="Envoyer" onBack={() => go('home')} green />
        <FgOffline isOnline={isOnline} />
        <div style={{ padding: 20 }}>
          <div style={{ ...card, padding: 28, textAlign: 'center', marginBottom: 16, animation: 'fg-scale 0.3s ease' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#ECFDF5', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30 }}>{'\u{1F4E4}'}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 20 }}>Synchronisation</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 24 }}>
              {[[uR.length, 'Releves', C.primary], [uP.length, 'Paiements', C.teal], [pendingSignals.length, 'Signaux', C.warning]].map(([v, l, c]) => (
                <div key={l as string} style={{ background: C.surface, borderRadius: 12, padding: 14, border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: c as string }}>{v as number}</div>
                  <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>{l as string}</div>
                </div>
              ))}
            </div>
            {total > 0 ? <Btn label={loading ? 'Envoi...' : `Envoyer (${total})`} full bg={isOnline ? C.gradGreen : undefined} disabled={loading || !isOnline} onClick={handleUpload} icon={'\u{1F680}'} />
              : <p style={{ color: C.textMuted, fontSize: 13 }}>Rien a envoyer</p>}
          </div>
          {uR.length > 0 && <div style={{ ...card, padding: 16, marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.textSec, marginBottom: 8 }}>Releves ({uR.length})</div>
            {uR.slice(0, 8).map((r, i) => (
              <div key={i} style={{ fontSize: 12, padding: '6px 0', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', color: C.textSec }}>
                <span>{r.abonne_nom}</span><span style={{ color: C.primary, fontWeight: 600 }}>{r.newindex}</span></div>))}
            {uR.length > 8 && <div style={{ fontSize: 10, color: C.textMuted, marginTop: 6 }}>+{uR.length - 8} autres</div>}
          </div>}
          {uP.length > 0 && <div style={{ ...card, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.textSec, marginBottom: 8 }}>Paiements ({uP.length})</div>
            {uP.slice(0, 8).map((p, i) => (
              <div key={i} style={{ fontSize: 12, padding: '6px 0', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', color: C.textSec }}>
                <span>{p.abonne || p.factureref}</span><span style={{ color: C.success, fontWeight: 600 }}>{p.montant.toLocaleString('fr-FR')} F</span></div>))}
          </div>}
        </div><FgLoader loading={loading} /><FgToast msg={msg} showNav={showNav} />
      </div>
    );
  }

  // ==================== UPLOAD OK ====================
  if (screen === 'upload-ok') {
    return (
      <div style={{ minHeight: '100vh', background: '#FFFFFF', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 30 }}>
        <div style={{ animation: 'fg-check 0.5s ease' }}>
          <div style={{ width: 100, height: 100, borderRadius: '50%', background: C.gradGreen, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 12px 40px rgba(33,150,243,0.25)', marginBottom: 24 }}>
            <span style={{ fontSize: 50, color: C.white }}>{'\u2713'}</span>
          </div>
        </div>
        <div style={{ color: C.text, fontSize: 24, fontWeight: 800, marginBottom: 8 }}>Envoi reussi</div>
        <div style={{ color: C.textSec, fontSize: 14, textAlign: 'center', marginBottom: 32 }}>
          {uploadResult.releves > 0 && <div>{uploadResult.releves} releve(s)</div>}
          {uploadResult.paiements > 0 && <div>{uploadResult.paiements} paiement(s)</div>}
          {uploadResult.signals > 0 && <div>{uploadResult.signals} signalisation(s)</div>}
        </div>
        <Btn label="Retour" bg={C.gradGreen} onClick={() => go('home')} icon={'\u2190'} />
      </div>
    );
  }

  // ==================== FALLBACK ====================
  return (
    <FgPageWrap {...pageWrapProps}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 }}>
        <Btn label="Accueil" onClick={() => go('home')} />
      </div>
    </FgPageWrap>
  );
};

export default Mobile;
